import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [summary, setSummary]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (search)       params.set('search', search);
    const res = await API(`/api/owner/enquiries?${params}`);
    const d = await res.json();
    if (!res.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
    setEnquiries(d.enquiries || []);
    setSummary(d.summary || []);
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const statusColor = (s) => ({
    new:          ['#dbeafe', '#1e40af'],
    open:         ['#d1fae5', '#065f46'],
    in_progress:  ['#fef3c7', '#92400e'],
    closed:       ['#f3f4f6', '#6b7280'],
    converted:    ['#ede9fe', '#5b21b6'],
  }[s] || ['#f3f4f6', '#6b7280']);

  const summaryTotal = summary.reduce((a, s) => a + parseInt(s.count), 0);

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Enquiries</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>All inbound enquiries across the platform</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Status summary pills */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilterStatus('')}
            style={{ ...pillBtn, fontWeight: !filterStatus ? 700 : 400, background: !filterStatus ? '#1e40af' : '#e0e7ff', color: !filterStatus ? '#fff' : '#1e40af' }}
          >
            All ({summaryTotal})
          </button>
          {summary.map(s => {
            const [bg, text] = statusColor(s.status);
            const active = filterStatus === s.status;
            return (
              <button key={s.status} onClick={() => setFilterStatus(s.status)} style={{
                ...pillBtn,
                background: active ? text : bg,
                color: active ? '#fff' : text,
                fontWeight: active ? 700 : 500,
              }}>
                {s.status.replace('_', ' ')} ({s.count})
              </button>
            );
          })}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <input
            placeholder="Search name, email or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : enquiries.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No enquiries found</div>
          ) : (
            enquiries.map(e => {
              const [bg, text] = statusColor(e.status);
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <div
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 16px', cursor: 'pointer', ':hover': { background: '#f9fafb' } }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{e.full_name || e.name || '—'}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>{e.email} {e.phone ? `· ${e.phone}` : ''}</div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', minWidth: '140px' }}>{e.company_name || '—'}</div>
                    <span style={{ background: bg, color: text, padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize', minWidth: '90px', textAlign: 'center' }}>
                      {(e.status || '').replace('_', ' ')}
                    </span>
                    <div style={{ fontSize: '12px', color: '#9ca3af', minWidth: '90px', textAlign: 'right' }}>
                      {e.created_at ? new Date(e.created_at).toLocaleDateString('en-GB') : '—'}
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '16px' }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                  {isOpen && (
                    <div style={{ padding: '16px 20px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                        {[
                          ['Email', e.email],
                          ['Phone', e.phone],
                          ['Company', e.company_name],
                          ['Assigned To', e.assigned_to_name || 'Unassigned'],
                          ['Loan Amount', e.loan_amount ? `£${parseFloat(e.loan_amount).toLocaleString()}` : '—'],
                          ['Loan Purpose', e.loan_purpose],
                          ['Property Value', e.property_value ? `£${parseFloat(e.property_value).toLocaleString()}` : '—'],
                          ['Created', e.created_at ? new Date(e.created_at).toLocaleString('en-GB') : '—'],
                          ['Source', e.source],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '2px' }}>{label}</div>
                            <div style={{ fontSize: '13px', color: '#374151' }}>{value}</div>
                          </div>
                        ))}
                      </div>
                      {e.message && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Message</div>
                          <div style={{ fontSize: '13px', color: '#374151', background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>{e.message}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}

const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '300px' };
const pillBtn = { border: 'none', borderRadius: '999px', padding: '6px 16px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
